import { z } from 'zod';
import { WHATSAPP_CAPTION_MAX_LENGTH, WHATSAPP_CONNECTION_STATUSES, WHATSAPP_CONVERSATION_PAGE_SIZE, WHATSAPP_CONVERSATION_STATUSES, WHATSAPP_HEADER_TYPES, WHATSAPP_MESSAGE_DIRECTIONS, WHATSAPP_MESSAGE_MAX_LENGTH, WHATSAPP_MESSAGE_PAGE_SIZE, WHATSAPP_MESSAGE_STATUSES, WHATSAPP_MESSAGE_TYPES, WHATSAPP_OPT_IN_SOURCES, WHATSAPP_OPT_IN_STATUSES, WHATSAPP_TEMPLATE_CATEGORIES, WHATSAPP_TEMPLATE_STATUSES, WHATSAPP_TEMPLATE_VARIABLE_SOURCES, } from '../constants/whatsapp.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { paginationMetaSchema } from './pagination.js';
import { normalizeWhatsAppPhoneNumber } from '../utils/whatsapp-phone.js';
export const whatsAppProviderSchema = z.enum(['CLOUD_API', 'BAILEYS']);
export const whatsAppQrCapabilitySchema = z.object({ available: z.boolean() });
export const whatsAppConnectionStatusSchema = z.enum(WHATSAPP_CONNECTION_STATUSES);
export const whatsAppConversationStatusSchema = z.enum(WHATSAPP_CONVERSATION_STATUSES);
export const whatsAppMessageDirectionSchema = z.enum(WHATSAPP_MESSAGE_DIRECTIONS);
export const whatsAppMessageTypeSchema = z.enum(WHATSAPP_MESSAGE_TYPES);
export const whatsAppMessageStatusSchema = z.enum(WHATSAPP_MESSAGE_STATUSES);
export const whatsAppTemplateCategorySchema = z.enum(WHATSAPP_TEMPLATE_CATEGORIES);
export const whatsAppTemplateStatusSchema = z.enum(WHATSAPP_TEMPLATE_STATUSES);
export const whatsAppHeaderTypeSchema = z.enum(WHATSAPP_HEADER_TYPES);
export const whatsAppOptInStatusSchema = z.enum(WHATSAPP_OPT_IN_STATUSES);
export const whatsAppOptInSourceSchema = z.enum(WHATSAPP_OPT_IN_SOURCES);
/** E.164: führendes Plus, Länderkennzahl ohne führende Null, insgesamt 8–15 Ziffern. */
export const e164PhoneSchema = z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/u, 'Erwartet wird eine Telefonnummer im Format +491701234567.');
export const whatsAppQrSessionSchema = z.object({
    status: z.enum(['DISCONNECTED', 'CONNECTING', 'QR_READY', 'CONNECTED', 'EXPIRED', 'ERROR']),
    connectedPhoneNumber: e164PhoneSchema.nullable(),
    qrDataUrl: z
        .string()
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/u)
        .nullable(),
    qrExpiresAt: isoDateTimeSchema.nullable(),
    error: z.string().nullable(),
});
export const whatsAppCustomerPhoneListSchema = z.object({
    data: z.array(z.object({
        phoneNumber: e164PhoneSchema,
        customerContactId: idSchema.nullable(),
        label: z.string(),
        source: z.enum(['CUSTOMER', 'CONTACT_MOBILE', 'CONTACT_PHONE']),
    })),
});
export const whatsAppHistoryImportResultSchema = z.object({
    requested: z.boolean(),
    reason: z.enum(['PAIRING_REQUIRED']).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Konfiguration
// ---------------------------------------------------------------------------
/**
 * Geheimnisse werden nie zurückgegeben. Statt des Klartexts meldet die API, ob
 * ein Wert hinterlegt ist, sowie die letzten Stellen als Wiedererkennung.
 */
export const whatsAppSecretStateSchema = z.object({
    isSet: z.boolean(),
    hint: z.string().nullable(),
});
export const whatsAppConfigSchema = z.object({
    phoneNumberId: z.string().nullable(),
    wabaId: z.string().nullable(),
    appId: z.string().nullable(),
    businessPhoneNumber: z.string().nullable(),
    displayPhoneNumberName: z.string().nullable(),
    webhookVerifyToken: z.string(),
    webhookCallbackUrl: z.string().url(),
    accessToken: whatsAppSecretStateSchema,
    appSecret: whatsAppSecretStateSchema,
    isConfigured: z.boolean(),
    connectionStatus: whatsAppConnectionStatusSchema,
    connectionError: z.string().nullable(),
    lastWebhookReceivedAt: isoDateTimeSchema.nullable(),
    lastVerifiedAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
});
export const whatsAppConfigUpdateSchema = z.object({
    phoneNumberId: z.string().trim().min(1).max(64),
    wabaId: z.string().trim().min(1).max(64),
    appId: z.string().trim().min(1).max(64),
    businessPhoneNumber: e164PhoneSchema,
    displayPhoneNumberName: z.string().trim().min(1).max(120),
    /** Leer lassen bewahrt das gespeicherte Geheimnis. */
    accessToken: z.string().trim().min(20).max(512).optional(),
    appSecret: z.string().trim().min(16).max(256).optional(),
    /** Erzeugt einen neuen Prüfstring für den Meta-Handshake. */
    rotateVerifyToken: z.boolean().default(false),
});
export const whatsAppConnectionTestSchema = z.object({
    isConnected: z.boolean(),
    connectionStatus: whatsAppConnectionStatusSchema,
    verifiedName: z.string().nullable(),
    displayPhoneNumber: z.string().nullable(),
    qualityRating: z.string().nullable(),
    messagingLimit: z.string().nullable(),
    checkedAt: isoDateTimeSchema,
    message: z.string(),
});
// ---------------------------------------------------------------------------
// Konversationen
// ---------------------------------------------------------------------------
export const whatsAppConversationSchema = z.object({
    provider: whatsAppProviderSchema.optional(),
    baileysAccountId: idSchema.nullable().optional(),
    baileysAccountLabel: z.string().nullable().optional(),
    id: idSchema,
    phoneNumber: z.string(),
    contactName: z.string().nullable(),
    status: whatsAppConversationStatusSchema,
    customerId: idSchema.nullable(),
    customerName: z.string().nullable(),
    customerNumber: z.string().nullable(),
    customerContactId: idSchema.nullable(),
    customerContactName: z.string().nullable(),
    assignedUserId: idSchema.nullable(),
    assignedUserName: z.string().nullable(),
    unreadCount: z.number().int().min(0),
    lastMessageAt: isoDateTimeSchema.nullable(),
    lastMessagePreview: z.string().nullable(),
    lastMessageDirection: whatsAppMessageDirectionSchema.nullable(),
    windowExpiresAt: isoDateTimeSchema.nullable(),
    isWindowOpen: z.boolean(),
    optInStatus: whatsAppOptInStatusSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const whatsAppConversationScopes = ['ALL', 'MINE', 'UNASSIGNED', 'CLOSED'];
export const whatsAppConversationScopeSchema = z.enum(whatsAppConversationScopes);
export const whatsAppConversationListQuerySchema = z.object({
    provider: whatsAppProviderSchema.optional(),
    baileysAccountId: idSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(WHATSAPP_CONVERSATION_PAGE_SIZE),
    scope: whatsAppConversationScopeSchema.default('ALL'),
    q: z.string().trim().min(1).max(200).optional(),
    customerId: idSchema.optional(),
});
export const whatsAppConversationListResponseSchema = z.object({
    data: z.array(whatsAppConversationSchema),
    meta: paginationMetaSchema,
    counts: z.object({
        all: z.number().int().min(0),
        mine: z.number().int().min(0),
        unassigned: z.number().int().min(0),
        closed: z.number().int().min(0),
        unread: z.number().int().min(0),
    }),
});
export const whatsAppConversationAssignSchema = z.object({
    assignedUserId: idSchema.nullable(),
});
export const whatsAppConversationStatusUpdateSchema = z.object({
    status: whatsAppConversationStatusSchema,
});
export const whatsAppConversationStartSchema = z.object({
    provider: whatsAppProviderSchema.default('CLOUD_API'),
    baileysAccountId: idSchema.optional(),
    customerId: idSchema.optional(),
    customerContactId: idSchema.optional(),
    phoneNumber: z.preprocess((value) => (typeof value === 'string' ? (normalizeWhatsAppPhoneNumber(value) ?? value) : value), e164PhoneSchema),
    contactName: z.string().trim().min(1).max(120).optional(),
});
/** Benutzer, denen ein Chat zugewiesen werden kann. */
export const whatsAppAgentSchema = z.object({
    id: idSchema,
    name: z.string(),
    email: z.string().email(),
});
export const whatsAppAgentListSchema = z.object({
    data: z.array(whatsAppAgentSchema),
});
// ---------------------------------------------------------------------------
// Nachrichten
// ---------------------------------------------------------------------------
export const whatsAppMessageSchema = z.object({
    id: idSchema,
    conversationId: idSchema,
    wamid: z.string().nullable(),
    direction: whatsAppMessageDirectionSchema,
    type: whatsAppMessageTypeSchema,
    status: whatsAppMessageStatusSchema,
    bodyText: z.string().nullable(),
    mediaUrl: z.string().nullable(),
    mediaMimeType: z.string().nullable(),
    mediaFileName: z.string().nullable(),
    mediaSizeBytes: z.number().int().min(0).nullable(),
    templateName: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    errorMessage: z.string().nullable(),
    senderUserId: idSchema.nullable(),
    senderUserName: z.string().nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    deliveredAt: isoDateTimeSchema.nullable(),
    readAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const whatsAppMessageListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(WHATSAPP_MESSAGE_PAGE_SIZE),
});
export const whatsAppMessageListResponseSchema = z.object({
    data: z.array(whatsAppMessageSchema),
    meta: paginationMetaSchema,
    conversation: whatsAppConversationSchema,
});
export const whatsAppSendTextSchema = z.object({
    conversationId: idSchema,
    body: z.string().trim().min(1).max(WHATSAPP_MESSAGE_MAX_LENGTH),
});
export const whatsAppSendDocumentSchema = z.object({
    conversationId: idSchema,
    documentId: idSchema,
    caption: z.string().trim().max(WHATSAPP_CAPTION_MAX_LENGTH).optional(),
});
export const whatsAppTemplateParameterSchema = z.object({
    position: z.number().int().min(1).max(50),
    value: z.string().trim().min(1).max(1024),
});
export const whatsAppSendTemplateSchema = z.object({
    conversationId: idSchema,
    templateId: idSchema,
    parameters: z.array(whatsAppTemplateParameterSchema).max(50).default([]),
    /** Beleg, dessen PDF als Kopfzeilen-Dokument angehängt wird. */
    documentId: idSchema.optional(),
});
// ---------------------------------------------------------------------------
// Vorlagen
// ---------------------------------------------------------------------------
export const whatsAppTemplateButtonSchema = z.object({
    type: z.string(),
    text: z.string(),
    url: z.string().nullable(),
    phoneNumber: z.string().nullable(),
});
export const whatsAppTemplateVariableSchema = z.object({
    position: z.number().int().min(1).max(50),
    source: z.enum(WHATSAPP_TEMPLATE_VARIABLE_SOURCES),
    label: z.string().min(1).max(120),
});
export const whatsAppTemplateSchema = z.object({
    id: idSchema,
    metaTemplateId: z.string().nullable(),
    name: z.string(),
    language: z.string(),
    category: whatsAppTemplateCategorySchema,
    status: whatsAppTemplateStatusSchema,
    headerType: whatsAppHeaderTypeSchema,
    headerContent: z.string().nullable(),
    bodyText: z.string(),
    footerText: z.string().nullable(),
    buttons: z.array(whatsAppTemplateButtonSchema),
    variableMapping: z.array(whatsAppTemplateVariableSchema),
    placeholders: z.array(z.number().int().min(1)),
    rejectedReason: z.string().nullable(),
    syncedAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
});
export const whatsAppTemplateListQuerySchema = z.object({
    status: whatsAppTemplateStatusSchema.optional(),
    category: whatsAppTemplateCategorySchema.optional(),
    approvedOnly: z.preprocess((value) => {
        if (value === 'true') {
            return true;
        }
        if (value === 'false') {
            return false;
        }
        return value;
    }, z.boolean().default(false)),
});
export const whatsAppTemplateListSchema = z.object({
    data: z.array(whatsAppTemplateSchema),
});
export const whatsAppTemplateSyncResultSchema = z.object({
    fetched: z.number().int().min(0),
    created: z.number().int().min(0),
    updated: z.number().int().min(0),
    removed: z.number().int().min(0),
    syncedAt: isoDateTimeSchema,
});
export const whatsAppTemplateMappingUpdateSchema = z.object({
    variableMapping: z.array(whatsAppTemplateVariableSchema).max(50),
});
// ---------------------------------------------------------------------------
// Einwilligung (DSGVO)
// ---------------------------------------------------------------------------
export const whatsAppOptInSchema = z.object({
    status: whatsAppOptInStatusSchema,
    optInDate: isoDateTimeSchema.nullable(),
    source: whatsAppOptInSourceSchema.nullable(),
});
export const whatsAppOptInUpdateSchema = z.object({
    status: whatsAppOptInStatusSchema,
    source: whatsAppOptInSourceSchema.default('MANUAL'),
});
// ---------------------------------------------------------------------------
// Webhook
// ---------------------------------------------------------------------------
/** Handshake-Parameter, die Meta beim Einrichten der Callback-URL sendet. */
export const whatsAppWebhookVerifyQuerySchema = z.object({
    'hub.mode': z.string(),
    'hub.verify_token': z.string(),
    'hub.challenge': z.string(),
});
const webhookProfileSchema = z.object({ name: z.string().optional() }).optional();
const webhookContactSchema = z.object({
    wa_id: z.string(),
    profile: webhookProfileSchema,
});
const webhookMediaSchema = z.object({
    id: z.string(),
    mime_type: z.string().optional(),
    sha256: z.string().optional(),
    caption: z.string().optional(),
    filename: z.string().optional(),
    voice: z.boolean().optional(),
});
const webhookMessageSchema = z.object({
    id: z.string(),
    from: z.string(),
    timestamp: z.string(),
    type: z.string(),
    text: z.object({ body: z.string() }).optional(),
    image: webhookMediaSchema.optional(),
    document: webhookMediaSchema.optional(),
    audio: webhookMediaSchema.optional(),
    video: webhookMediaSchema.optional(),
    sticker: webhookMediaSchema.optional(),
    location: z
        .object({
        latitude: z.number(),
        longitude: z.number(),
        name: z.string().optional(),
        address: z.string().optional(),
    })
        .optional(),
    button: z.object({ text: z.string().optional(), payload: z.string().optional() }).optional(),
    interactive: z
        .object({
        type: z.string().optional(),
        button_reply: z.object({ id: z.string(), title: z.string() }).optional(),
        list_reply: z.object({ id: z.string(), title: z.string() }).optional(),
    })
        .optional(),
    context: z.object({ id: z.string().optional() }).optional(),
});
const webhookStatusSchema = z.object({
    id: z.string(),
    status: z.string(),
    timestamp: z.string(),
    recipient_id: z.string().optional(),
    errors: z
        .array(z.object({
        code: z.number().optional(),
        title: z.string().optional(),
        message: z.string().optional(),
    }))
        .optional(),
});
export const whatsAppWebhookPayloadSchema = z.object({
    object: z.string(),
    entry: z.array(z.object({
        id: z.string(),
        changes: z.array(z.object({
            field: z.string(),
            value: z.object({
                messaging_product: z.string().optional(),
                metadata: z
                    .object({ display_phone_number: z.string().optional(), phone_number_id: z.string() })
                    .optional(),
                contacts: z.array(webhookContactSchema).optional(),
                messages: z.array(webhookMessageSchema).optional(),
                statuses: z.array(webhookStatusSchema).optional(),
            }),
        })),
    })),
});
//# sourceMappingURL=whatsapp.js.map